import {inclusiveLastDay} from '@redstack/shared'

type NameableUser = {
	firstName?: string | null
	lastName?: string | null
	email?: string | null
}

const RELATIVE_TIME_FMT = new Intl.RelativeTimeFormat('en', {numeric: 'auto'})
const TIME_FMT = new Intl.DateTimeFormat('en-US', {
	hour: 'numeric',
	minute: '2-digit',
	hour12: true,
})
const MONTH_DAY_FMT = new Intl.DateTimeFormat('en-US', {
	month: 'short',
	day: 'numeric',
})
const CAD_FMT = new Intl.NumberFormat('en-CA', {
	style: 'currency',
	currency: 'CAD',
	maximumFractionDigits: 0,
})
// Formatters for date-ONLY values (`@db.Date` columns: cycle planned dates,
// eventually issueDate). These arrive as UTC-midnight ISO strings, so they
// must render in UTC — local-time formatting shifts them to the previous day
// for any timezone west of Greenwich (Thursday cadence displays as Wednesday).
// Timestamp fields (createdAt etc.) keep the local-time formatters above.
const UTC_MONTH_DAY_FMT = new Intl.DateTimeFormat('en-US', {
	month: 'short',
	day: 'numeric',
	timeZone: 'UTC',
})
const UTC_WEEKDAY_DATE_FMT = new Intl.DateTimeFormat('en-US', {
	weekday: 'short',
	month: 'short',
	day: 'numeric',
	timeZone: 'UTC',
})

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
	['year', 60 * 60 * 24 * 365],
	['month', 60 * 60 * 24 * 30],
	['week', 60 * 60 * 24 * 7],
	['day', 60 * 60 * 24],
	['hour', 60 * 60],
	['minute', 60],
]

/**
 * Format a user's name for display
 * Returns full name if available, otherwise email, otherwise 'Unknown'
 */
export function formatUserName(user: NameableUser): string {
	if (user.firstName || user.lastName) {
		return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
	}
	return user.email ?? 'Unknown'
}

/**
 * Format a slug or enum string as Title Case for display
 * Handles both DB format (DISTRICT_OF_NORTH_VANCOUVER) and slug format (district-of-north-vancouver)
 */
export function formatTitleCase(value: string): string {
	return value
		.replace(/_/g, ' ')
		.replace(/-/g, ' ')
		.toLowerCase()
		.replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Format a North American 10-digit phone number as `(XXX) XXX-XXXX`.
 * Returns the original string for non-10-digit input, or '-' for null.
 */
export function formatPhone(phone: string | null | undefined): string {
	if (!phone) return '-'
	const cleaned = phone.replace(/\D/g, '')
	if (cleaned.length === 10) {
		return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
	}
	return phone
}

/**
 * Format a date as relative time (e.g., "2 days ago", "3 weeks ago")
 */
export function formatRelativeDate(date: string | Date): string {
	const now = Date.now()
	const then = new Date(date).getTime()
	const diffSeconds = Math.round((then - now) / 1000)

	for (const [unit, secondsInUnit] of RELATIVE_UNITS) {
		if (Math.abs(diffSeconds) >= secondsInUnit) {
			const value = Math.round(diffSeconds / secondsInUnit)
			return RELATIVE_TIME_FMT.format(value, unit)
		}
	}

	return 'just now'
}

/**
 * Returns uppercase initials from a user-like object (first + last name, or email fallback).
 */
export function getInitials(user: NameableUser): string {
	const first = user.firstName?.[0] ?? ''
	const last = user.lastName?.[0] ?? ''
	return (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || '?'
}

/**
 * Format a date as an exact timestamp for activity/history entries.
 * Today → "Today at 2:34 PM", Yesterday → "Yesterday at 10:15 AM",
 * Same year → "Mar 5 at 2:34 PM", Different year → "Mar 5, 2025 at 2:34 PM"
 */
export function formatEventTimestamp(date: string | Date): string {
	const d = new Date(date)
	const now = new Date()
	const time = TIME_FMT.format(d)

	const sameDay = (a: Date, b: Date) =>
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()

	if (sameDay(d, now)) return `Today at ${time}`

	const yesterday = new Date(now)
	yesterday.setDate(yesterday.getDate() - 1)
	if (sameDay(d, yesterday)) return `Yesterday at ${time}`

	const monthDay = MONTH_DAY_FMT.format(d)

	if (d.getFullYear() === now.getFullYear()) {
		return `${monthDay} at ${time}`
	}
	return `${monthDay}, ${d.getFullYear()} at ${time}`
}

/**
 * Format a date-only value (`@db.Date`, UTC midnight) as "Jun 4".
 * Use for cycle planned dates — NOT for timestamps (see formatEventTimestamp).
 */
export function formatDateOnly(date: string | Date): string {
	return UTC_MONTH_DAY_FMT.format(new Date(date))
}

/**
 * Format a date-only value (`@db.Date`, UTC midnight) as "Thu, Jun 4".
 */
export function formatDateOnlyLong(date: string | Date): string {
	return UTC_WEEKDAY_DATE_FMT.format(new Date(date))
}

/**
 * Format a date-only range as "Jun 4 → Jun 18". Both ends are rendered
 * verbatim — for cycle planned windows (half-open, exclusive end) use
 * `formatCycleWindow` instead.
 */
export function formatDateOnlyRange(
	start: string | Date,
	end: string | Date,
): string {
	return `${formatDateOnly(start)} → ${formatDateOnly(end)}`
}

/**
 * The cycle's last calendar day, formatted as "Jul 8". Cycle windows are
 * half-open — `plannedEndDate` is the NEXT cycle's first day — so any copy
 * describing the cycle's own span ("Ended …", "runs until …", ranges) must
 * show `plannedEndDate − 1`. Rendering the raw end date reads as if it
 * belongs to the cycle ("Jul 2 → Jul 9, Day 7/7" on Jul 8).
 */
export function formatCycleLastDay(exclusiveEnd: string | Date): string {
	return formatDateOnly(inclusiveLastDay(new Date(exclusiveEnd)))
}

/**
 * Format a cycle's planned window as "Jul 2 → Jul 8" (inclusive last day).
 */
export function formatCycleWindow(
	start: string | Date,
	exclusiveEnd: string | Date,
): string {
	return `${formatDateOnly(start)} → ${formatCycleLastDay(exclusiveEnd)}`
}

/**
 * Format a currency value as CAD (no cents — permit values don't need cent precision)
 */
export function formatCAD(value: number | null): string {
	if (value === null) return '-'
	return CAD_FMT.format(value)
}

/**
 * Format a currency value as compact CAD (e.g., "$4.2M", "$920K", "$800")
 * Used on the Today checklist where space is tight.
 */
export function formatCompactCAD(value: number | null): string {
	if (value === null) return '-'
	if (value >= 1_000_000) {
		const millions = value / 1_000_000
		const isWhole = millions % 1 === 0
		return `$${isWhole ? millions.toFixed(0) : millions.toFixed(1)}M`
	}
	if (value >= 1_000) {
		return `$${Math.round(value / 1_000)}K`
	}
	return `$${value}`
}
